import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useRouter, useSearchParams } from 'src/routes/hooks';
// assets
import { EmailInboxIcon } from 'src/assets/icons';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFCode, RHFTextField } from 'src/components/hook-form';
import { toast } from 'react-toastify';
import {
  useVerifyEmailMutation,
  useRegularUserResendOtpMutation,
} from '../../../store/Reducer/auth';

export default function JWTVerifyView() {
  const router = useRouter();
  const [verifyEmail] = useVerifyEmailMutation();
  const [resendOtp] = useRegularUserResendOtpMutation();
  const searchParams = useSearchParams();

  const email = searchParams.get('email');

  const [countdown, setCountdown] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [intervalId]);

  // Start countdown timer (60s)
  const startCountdown = (duration = 60) => {
    setCountdown(duration);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setIntervalId(id);
  };

  const VerifySchema = Yup.object().shape({
    code: Yup.string()
      .min(6, 'Code must be at least 6 digits')
      .required('Code is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
  });

  const defaultValues = {
    code: '',
    email: email || '',
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(VerifySchema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await verifyEmail({ email: data.email, otp: data.code }).unwrap();
      toast.success('Email verified successfully!');
      setOpenSuccessDialog(true);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to verify email');
    }
  });

  const handleContinueToLogin = () => {
    router.replace(paths.auth.jwt.login);
  };

  const handleResendCode = useCallback(async () => {
    if (isResending) return;
    try {
      setIsResending(true);
      await resendOtp({ email: values.email }).unwrap();
      toast.success('OTP resent successfully!');
      startCountdown(60);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  }, [resendOtp, values.email, isResending]);

  const renderForm = (
    <Stack spacing={3} alignItems="center">
      <RHFTextField
        name="email"
        label="Email"
        placeholder="example@gmail.com"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          sx: {
            pointerEvents: 'none',
            '&:hover fieldset': { borderColor: 'transparent !important' },
            '&.Mui-focused fieldset': { borderColor: 'transparent !important' },
            backgroundColor: 'background.paper',
          },
        }}
      />

      <RHFCode name="code" />

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        loading={isSubmitting}
      >
        Verify
      </LoadingButton>

      <Typography variant="body2">
        Don’t have a code?{' '}
        <Link
          variant="subtitle2"
          onClick={countdown === 0 && !isResending ? handleResendCode : undefined}
          sx={{
            cursor: countdown === 0 && !isResending ? 'pointer' : 'default',
            color: countdown === 0 && !isResending ? 'primary.main' : 'text.disabled',
            pointerEvents: countdown === 0 && !isResending ? 'auto' : 'none',
          }}
        >
          {isResending ? 'Sending...' : countdown > 0 ? `Resend code (${countdown}s)` : 'Resend code'}
        </Link>
      </Typography>

      <Link
        component={RouterLink}
        href={paths.auth.jwt.login}
        color="inherit"
        variant="subtitle2"
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
        }}
      >
        <Iconify icon="eva:arrow-ios-back-fill" width={16} />
        Return to sign in
      </Link>
    </Stack>
  );

  const renderHead = (
    <>
      <EmailInboxIcon sx={{ height: { md: 96, xs: 50 } }} />
      <Stack spacing={1} sx={{ my: 5 }}>
        <Typography variant="h3">Please check your email!</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          We’ve sent a 6-digit code to {email || 'your email'}. Enter it below to verify.
        </Typography>
      </Stack>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
      <Dialog
        open={openSuccessDialog}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 3,
            maxWidth: 400,
            textAlign: 'center',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '20px',
                bgcolor: '#de4d1b',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  width: 40,
                  height: 8,
                  bgcolor: 'rgba(222, 77, 27, 0.2)',
                  borderRadius: '50%',
                },
              }}
            >
              <Iconify icon="eva:checkmark-fill" width={40} sx={{ color: '#fff' }} />
            </Box>
          </Box>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            Account Created
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, px: 2 }}>
            Your account has been created successfully and submitted for admin approval. You will be notified via email once it has been approved.
          </Typography>
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={handleContinueToLogin}
            sx={{
              bgcolor: '#de4d1b',
              '&:hover': {
                bgcolor: '#c4411a',
              },
            }}
          >
            Continue
          </Button>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
}
